import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Circle, Ellipse, G, Line, Path, Text as SvgText } from 'react-native-svg';
import {
  GEARS,
  GEAR_META,
  SECTOR_LABELS,
  SECTORS,
  STAGE_LABELS,
  TOOL_NAMES,
} from '../domain/olmps';
import type { Sector, Stage } from '../domain/olmps';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii, SECTOR_COLOR } from '../v10/theme';
import { formatClockTime, formatLadderRungState, type DayEntry } from './useTodayData';
import { useSharedTodayData } from './TodayDataContext';
import type { Rung } from '../lib/stage-ladder';
import { useSettingColumn } from './settings/useSettingColumn';
import { useOracleProjects, type OracleProject, type OracleTaskLink } from './useOracleProjects';
import { activeSpokes, normalizeConfig, type RingSystemId, type Spoke } from './sectorConfig';
import { GEAR_ANCHOR, type GearId } from './ascension';
import { computeProgress, todayKey } from './deity-progress';
import { deityLevelData } from './deity-data';

/** Gradient glass fill + 1px inner top highlight — the compass's depth recipe
 *  (never a flat block). `tone` picks glass / strong (near-opaque) / gold. */
function GlassFill({ radius = 0, topOnly = false, tone = 'glass' }: { radius?: number; topOnly?: boolean; tone?: 'glass' | 'strong' | 'gold' }) {
  const { t } = useTheme();
  const cols: [string, string] =
    tone === 'gold' ? [t.goldBright, t.gold] : tone === 'strong' ? [t.navyRaised, t.navy] : t.panelGradient;
  const r = topOnly
    ? { borderTopLeftRadius: radius, borderTopRightRadius: radius }
    : { borderRadius: radius };
  return (
    <>
      <LinearGradient
        colors={cols}
        start={{ x: 0.2, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, r]}
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          left: radius * 0.4,
          right: radius * 0.4,
          height: 1,
          backgroundColor: tone === 'gold' ? 'rgba(255,255,255,0.35)' : t.panelHighlight,
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// OLMPS "Order Compass" (ASTRA) (mobile) — DEMO-DRIVEN.
//
// A faithful, self-contained port of the approved prototype HTML: orbital view
// stacked above a single navigable ORDER orb. Click a node → its sector
// orbitally rotates down to 6 o'clock under the orb, with a dashed connector.
// Left/right = swap sector; up/down = change the orb's STAGE (zoom pulse). Tap
// an orb value cell → tool popup (note box + "Open {TOOL}" → onOpenTool).
// Double-tap a nav arrow → child-creation stub (toast).
//
// All node drift, agenda tasks, ORDER cell content, and the gear→tool mapping
// are the prototype's exact sample data (constants below) — NOT Supabase — so
// it renders instantly and behaves identically to the design. Re-wiring to
// live data is a later step: swap DEMO_AGENDA / SAMPLE_CELLS.
//
// CompassCore: Exported reusable component that encapsulates the compass
// orbital rendering + arrow navigation + orb interaction. The compact flag
// shrinks sizing/hides fullscreen chrome. Use in CompassView (fullscreen) or
// AstraWidget (dashboard compact). ─────────────────────────────────────────────────────────────────────────

type DriftColor = 'lime' | 'amber' | 'red';

// Orb ring order, inner→outer: level 0 = Endeavour, 4 = Step.
const ORB_STAGES: Stage[] = ['E', 'G', 'A', 'T', 'S'];

/** STAGE ladder rung (src/lib/stage-ladder.ts) each compass ring composes
 *  into. Ring tinting + the orb popover's "rung · state" label are driven
 *  off ladder.rungs[STAGE_TO_RUNG[stage]] — see useTodayData's `ladder`. */
const STAGE_TO_RUNG: Record<Stage, Rung> = { S: 'step', T: 'today', A: 'aim', G: 'goal', E: 'endeavour' };
/** Rings whose live pace (onTrack/behind-pace) tints the ring stroke — the
 *  Step ring is excluded (its window is hourly and too granular to tint). */
const TINTED_STAGES: Stage[] = ['T', 'A', 'G', 'E'];
const RX_RATIO = [0.182, 0.387, 0.592, 0.796, 1.0]; // inner→outer
const RY_FACTOR_DEFAULT = 0.375; // default ellipse tilt (ry / rx); user-adjustable
const RY_FACTOR_MIN = 0.04; // near-flat: the compass reads as an edge-on disc
const RY_FACTOR_MAX = 1.0; // full upright circle
const NODE_R = 7;
const NODE_HIT_R = 18; // tap-target radius around each disc node (36px diameter)
const DPAD = 132; // overall D-pad controller size (square)
const DPAD_BTN = 42; // each arrow/centre button diameter
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.6;
const ZOOM_STEP = 0.2;
const MAXOFF = 9; // max tangential drift, degrees

const SECTOR_FILL: Record<Sector, string> = {
  S: SECTOR_COLOR.S, // Social
  H: SECTOR_COLOR.H, // Health
  A: SECTOR_COLOR.A, // Admin
  P: SECTOR_COLOR.P, // Personal
  E: SECTOR_COLOR.E, // Economic
};

/** Re-hue built-in spokes with the v10 sector palette (custom spokes keep theirs). */
const hueSpokes = (list: Spoke[]): Spoke[] =>
  list.map((sp) => (sp.key ? { ...sp, color: SECTOR_FILL[sp.key] } : sp));

/** Pixels a project stage orb pulls left per drift score point (0..4). */
const DRIFT_PX_PER_SCORE = 9;

/** Planetary glyph prefixed to each sector label. */
const SECTOR_GLYPH: Record<Sector, string> = {
  A: '♄', // Admin — Saturn
  E: '♃', // Economic — Jupiter
  H: '♂', // Health — Mars
  S: '♀', // Social — Venus
  P: '☿', // Personal — Mercury
};

// The compass has two ring systems: ORDER (the O/R/D/E/R prongs — always on)
// and SHAPE (life-area prongs — off until unlocked via config.shapeEnabled).
// STAGE is NOT a ring system: it's the chronological layer of ORDER, rendered
// as the concentric S/T/A/G/E orbits (ORB_STAGES) within each disc. Each
// system's spoke list (enabled built-ins + custom) comes from user config via
// activeSpokes(); see sectorConfig.ts. spokesFor() (below) hands each level
// its list.

/** Severity from drift magnitude — mirrors the prototype's sevColor(). */
function sevColor(mag: number): DriftColor {
  return mag < 0.12 ? 'lime' : mag < 0.4 ? 'amber' : 'red';
}

/** Lighten (amt>0, mix white) / darken (amt<0, mix black) a #rrggbb hex. */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  if (amt >= 0) {
    r += (255 - r) * amt;
    g += (255 - g) * amt;
    b += (255 - b) * amt;
  } else {
    r *= 1 + amt;
    g *= 1 + amt;
    b *= 1 + amt;
  }
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Per-ring shade of the sector colour: outer (Step) darkest → inner (Endeavour) lightest. */
const RING_SHADE: Record<Stage, number> = { E: 0.45, G: 0.25, A: 0.05, T: -0.15, S: -0.35 };

// ---------- Prototype sample data (verbatim from the approved HTML) ---------

/** Real per-sector drift: for each ring (inner→outer, E,G,A,T,S) the worst
 *  `drift.score` (0..4, tkneDrift.ts) across that sector's projects, as a 0..1
 *  magnitude. OracleProject.drift is in progression order (Step first), so ring
 *  i maps to drift[4 - i]. No projects → 0. */
type SectorDrift = Record<Sector, number[]>;
function buildSectorDrift(projects: Record<Sector, OracleProject[]>): SectorDrift {
  const out = {} as SectorDrift;
  for (const sector of SECTORS) {
    out[sector] = ORB_STAGES.map((_, ring) => {
      let worst = 0;
      for (const p of projects[sector] ?? []) worst = Math.max(worst, p.drift[4 - ring]?.score ?? 0);
      return worst / 4;
    });
  }
  return out;
}

// ---------- PROJECTS layer (the second "projects disc") ---------------------
// Real projects come from useOracleProjects (designs table) — see that hook
// for the OracleProject shape. Each sector holds N projects; each project is
// its own orbital lane within the sector's wedge, mirroring all five ring
// stages directly below the disc's nodes. `drift` (0..1) is neglect — 0 =
// aligned on the spoke; higher pulls the orb angularly out of the wedge
// (left). Real projects have no drift signal yet, so it's always 0.

/** ORDER prong (O,R,D,E,R) → tool, per the locked ORDER ring (spec 2026-07-21):
 *  Orient=Oracle(+Astra), Regulate=Numa, Direct=Tkne, Execute=(the doing), Revise=Clio. */
const DEMO_TOOL_BY_GEAR = ['oracle', 'numa', 'tkne', 'themis', 'clio'];

/** Sample ORDER cell content per sector (reused across all five STAGE rings).
 *  Fields = Objective · Requires · Directive · Execution (status circles) · Revise (review date). */
const SAMPLE_CELLS: Record<Sector, string[]> = {
  H: ['Sub-3:30 marathon', '8h sleep · fresh legs', '6×800m @ 3:20', '●●●○', 'Revise 8/22'],
  S: ['See friends weekly', 'Free Fri evening', 'Book the table', '●●○○', 'Revise 8/20'],
  A: ['Inbox zero', '30m daily block', 'File taxes', '●●●●', 'Revise 8/25'],
  P: ['Read 20 books', 'Kindle charged', '30m nightly', '●●●○', 'Revise 8/22'],
  E: ['Grow runway 12mo', 'Cut 2 subs', 'Ship v2', '●●○○', 'Revise 8/30'],
};

function buildSampleNotes(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const sector of SECTORS)
    for (const stage of ORB_STAGES)
      SAMPLE_CELLS[sector].forEach((txt, i) => {
        out[`${sector}|${stage}|${i}`] = txt;
      });
  return out;
}

const radOf = (deg: number) => (deg * Math.PI) / 180;
/** Angle of spoke `i` of `count`, evenly spaced around the disc with the first
 *  spoke at 6 o'clock (90°). `count` is user-controlled — enabled built-in
 *  sectors plus any custom ones — so the disc re-spaces as spokes are added or
 *  removed (5 spokes → 72°, 4 → 90°, …). */
function spokeAngle(i: number, count: number): number {
  return 90 + (360 / count) * i;
}
/** Drift for a spoke. Built-in spokes read the real per-sector drift; custom
 *  spokes have no drift signal, so they render flat/aligned (lime). */
function driftOfSpoke(table: SectorDrift, spoke: Spoke, stage: Stage): { mag: number; dir: number; color: DriftColor } {
  if (!spoke.key) return { mag: 0, dir: 1, color: 'lime' };
  const mag = table[spoke.key]?.[ORB_STAGES.indexOf(stage)] ?? 0;
  return { mag, dir: 1, color: sevColor(mag) };
}

// Agenda bottom-sheet dimensions. The header bar is always visible at the
// bottom; the body slides up (over the orb) when expanded, down when collapsed.
const AGENDA_HEADER_H = 50;
const AGENDA_BODY_H = 300;

// Hour rail range — 6am to 11pm covers the practical waking day.
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 23;

/** Entries whose window overlaps the given hour (rail row granularity). */
function entriesInHour(entries: DayEntry[], hour: number, base: Date): DayEntry[] {
  const slotStart = new Date(base);
  slotStart.setHours(hour, 0, 0, 0);
  const slotEnd = new Date(base);
  slotEnd.setHours(hour + 1, 0, 0, 0);
  return entries.filter((e) => e.start < slotEnd && e.end > slotStart);
}

function sameCalendarDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Current Mon–Sun week range containing `base` — mirrors
 *  useOracleProjects.ts's currentWeekRange exactly (Week strings mode). */
function currentWeekRange(base: Date): { start: Date; end: Date } {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  const start = new Date(d);
  start.setDate(d.getDate() + diffToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return { start, end };
}

// Double-tap window for agenda-rail task rows (highlight-project gesture).
const DOUBLE_TAP_MS = 350;
// How long a double-tap highlight stays lit.
const HIGHLIGHT_MS = 4000;
// Cap on visible task dots per project in Week mode (rest collapse to "+N").
const MAX_DOTS_PER_PROJECT = 8;
const TASK_DOT_R = 4;
const TASK_DOT_GAP = 14;

// ---- projects geometry. A project MIRRORS the disc's five ring nodes
// (E,G,A,T,S — ORB_STAGES): its five orbs sit DIRECTLY BELOW the disc's nodes —
// identical x, spacing, and size — dropped down by a fixed row offset.
// Successive projects mirror again, one row lower.
const PROJ_ROW_DROP = 34; // disc nodes → first project row
const PROJ_ROW_GAP = 30; // between successive project rows
// ---- Directions-step geometry. A project's orbs are its ORDER "Directions"
// steps (designs.basic_stages), an ARBITRARY count — not the fixed five stage
// rings. Sub-steps hang straight down from their step.
const SUB_DROP = 15; // step orb → first sub-step orb
const SUB_GAP = 14; // between successive sub-step orbs
const SUB_R = 4.2; // sub-step orb radius (smaller than a step's NODE_R)

/** Vertical space one project row needs: the row itself plus the tail of its
 *  deepest sub-step column, so rows never overlap. */
function projRowHeight(proj: OracleProject): number {
  const deepest = proj.steps.reduce((m, s) => Math.max(m, s.substeps.length), 0);
  return PROJ_ROW_GAP + (deepest > 0 ? SUB_DROP + (deepest - 1) * SUB_GAP : 0);
}

// ---------- CompassCore: Reusable orbital compass (fullscreen or compact) ---
/** CompassCore exports the orbital rendering + orb + arrow nav as a compact
 *  reusable unit. compact=true shrinks sizes and hides only the agenda sheet.
 *  The layer toggles and motion controls remain available in both layouts. */
/** Vertical centre of the fullscreen disc inside CompassCore's root (content
 *  paddingTop 12 + fixedCompassH / 2) so a host can anchor the compass on a
 *  point behind it. Mirrors the constants used below. */
export function compassFixedCenterY(width: number): number {
  const stageW = Math.min(width - 24, 420);
  const fixedH = Math.round((stageW / 2) * 0.9 * ZOOM_MAX * RY_FACTOR_MAX * 2 + 56);
  return 12 + fixedH / 2;
}

export function CompassCore({
  userId,
  onOpenTool,
  compact = false,
  system = 'order',
  scroll = true,
}: {
  userId: string | undefined;
  onOpenTool: (toolId: string) => void;
  compact?: boolean;
  system?: RingSystemId;
  /** false = the host positions the disc; the stage does not scroll. */
  scroll?: boolean;
}) {
  const { width } = useWindowDimensions();
  const { t, c } = useTheme();
  const styles = useStyles();
  const SEV_STROKE: Record<DriftColor, string> = { lime: t.onPace, amber: t.behind, red: t.lapsed };
  const { now, dayEntries, ladder } = useSharedTodayData();
  const { projects: oracleProjects, taskLinks, isDemo: projectsAreDemo } = useOracleProjects(userId);
  const sectorDrift = useMemo(() => buildSectorDrift(oracleProjects), [oracleProjects]);
  const driftOf = (spoke: Spoke, stage: Stage) => driftOfSpoke(sectorDrift, spoke, stage);

  // Every level's spokes are user-configured (enabled built-ins + custom spokes)
  // via user_settings.oracle_sector_config — this is what makes each disc gain/
  // lose spokes and re-space (5→72°, 4→90°, …). Edits made in the ORACLE tool
  // are mirrored here. spokesFor() hands each level its list.
  const { value: rawConfig } = useSettingColumn<unknown>(userId, 'oracle_sector_config', null);
  const compassConfig = useMemo(() => normalizeConfig(rawConfig), [rawConfig]);
  const shapeSpokes = useMemo(() => hueSpokes(activeSpokes(compassConfig, 'shape')), [compassConfig]);
  const orderSpokes = useMemo(() => hueSpokes(activeSpokes(compassConfig, 'order')), [compassConfig]);
  const spokesFor = useCallback(
    (sys: RingSystemId): Spoke[] => (sys === 'shape' ? shapeSpokes : orderSpokes),
    [shapeSpokes, orderSpokes]
  );
  // SHAPE disc only renders once unlocked (config.shapeEnabled — set via
  // ASTRA acquisition or the manual settings toggle). ORDER is always on.
  const shapeUnlocked = compassConfig.shapeEnabled;
  /** Index of a built-in Sector within the live SHAPE spoke list, or -1 if that
   *  sector is currently disabled (so its projects / task strings are hidden). */
  const shapeIndexOfSector = useCallback(
    (sector: Sector): number => shapeSpokes.findIndex((s) => s.key === sector),
    [shapeSpokes]
  );

  // ---- ORDER gear acquisition level (MOYRI point engine) -------------------
  // Each ORDER spoke is a gear; its progression is the anchor deity's
  // acquisition tier from deity-progress.computeProgress — consistency of the
  // user's selected principles, NOT the old STAGE ladder. tierIdx is
  // 0 STEP · 1 TODAY · 2 AIM · 3 GOAL · 4 SECURED, which lines up one-to-one
  // with the disc's five rings read OUTWARD-IN (S is the outer ring).
  const todayStr = todayKey();
  const gearTierIdx = useMemo(() => {
    const out = {} as Record<GearId, number>;
    for (const gear of Object.keys(GEAR_ANCHOR) as GearId[]) {
      const tool = GEAR_ANCHOR[gear];
      out[gear] = computeProgress(compassConfig.principleProgress[tool], deityLevelData(tool), todayStr, tool).tierIdx;
    }
    return out;
  }, [compassConfig.principleProgress, todayStr]);

  /** The gear a built-in ORDER spoke stands for — spoke ids are `order-<gear>`
   *  (sectorConfig ORDER_BUILTINS). Custom spokes have no gear. */
  const gearOfSpoke = (spoke: Spoke): GearId | null => {
    const gear = spoke.id.startsWith('order-') ? (spoke.id.slice('order-'.length) as GearId) : null;
    return gear && gear in GEAR_ANCHOR ? gear : null;
  };

  const [orbOpen, setOrbOpen] = useState(false);
  // Which of the stacked ring-system levels the open orb belongs to.
  const [curSystem, setCurSystem] = useState<RingSystemId>('order');
  // Per-level visibility — the ORDER / SHAPE toggles hide/show each level.
  const [levelOn, setLevelOn] = useState<Record<RingSystemId, boolean>>({
    order: true,
    shape: true,
  });
  // The open orb's spoke, identified by spoke id (Sector letter for built-ins,
  // custom.id for custom spokes) within the current level (curSystem).
  const [curSpokeId, setCurSpokeId] = useState<string | null>(null);
  const [curLevel, setCurLevel] = useState(4); // index into ORB_STAGES; 4 = Step
  const [systemScale, setSystemScale] = useState(1);
  // Vertical tilt of the compass ellipse (ry/rx). Lower = flatter/edge-on
  // (disc-like); adjusted by the D-pad up/down buttons.
  const [tilt, setTilt] = useState(RY_FACTOR_DEFAULT);
  // Runtime play/pause for the auto-spin (the D-pad centre button). Separate
  // from the persisted oracle_spin_enabled setting — this is a live toggle.
  const [spinPaused, setSpinPaused] = useState(false);
  // Persistent zoom level for the whole compass stage — the D-pad's zoom
  // buttons. Separate from `systemScale`, which is a transient orb-level pulse.
  const [zoom, setZoom] = useState(1);
  // Measured width of whatever hosts this compass (a dashboard band is much
  // narrower than the window), so the compact stage can span it exactly.
  const [hostW, setHostW] = useState(0);
  // The second "projects disc" under the compass — toggle-able.
  const [showProjects, setShowProjects] = useState(true); // TKNE layer (under SHAPE)
  // The original top-layer disc orbs (the per-ring drift nodes) — TELOS layer.
  const [showTelos, setShowTelos] = useState(true);
  // Task→project "strings" range — Today vs the whole current week.
  const [stringsRange, setStringsRange] = useState<'today' | 'week'>('today');
  // Double-tap on an agenda task row lights up its linked project/sector for
  // a few seconds — see handleAgendaEntryPress below.
  const [highlight, setHighlight] = useState<{ projectId: string | null; sector: Sector | null } | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const agendaTapTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [popup, setPopup] = useState<{ gearIdx: number } | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>(buildSampleNotes);
  const [toast, setToast] = useState<string | null>(null);

  const rotationRef = useRef(0);
  const [rotation, setRotationState] = useState(0);
  const rafRef = useRef<number | null>(null);
  const idleRafRef = useRef<number | null>(null);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const arrowTimers = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const orbScale = useRef(new Animated.Value(1)).current;
  const agendaY = useRef(new Animated.Value(AGENDA_BODY_H)).current; // start collapsed (body slid down)

  const setRotation = useCallback((v: number) => {
    rotationRef.current = v;
    setRotationState(v);
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (idleRafRef.current) cancelAnimationFrame(idleRafRef.current);
      [pulseTimer, toastTimer, highlightTimer].forEach((t) => t.current && clearTimeout(t.current));
      Object.values(arrowTimers.current).forEach((t) => t && clearTimeout(t));
      Object.values(agendaTapTimers.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  // Constant idle spin — one full 360° rotation every `spinSeconds` — that
  // runs only when spin is enabled AND no orb is open. Opening an orb
  // (orbOpen=true) pauses it; closing resumes. Speed/on-off come from the
  // ORACLE settings (user_settings.oracle_spin_enabled / oracle_spin_seconds).
  const { value: spinEnabled } = useSettingColumn<boolean>(userId, 'oracle_spin_enabled', true);
  const { value: spinSeconds } = useSettingColumn<number>(userId, 'oracle_spin_seconds', 15);
  useEffect(() => {
    if (orbOpen || !spinEnabled || spinPaused) return;
    const secs = spinSeconds > 0 ? spinSeconds : 15;
    const DEG_PER_MS = 360 / (secs * 1000);
    let last = Date.now();
    const tick = () => {
      const now = Date.now();
      setRotation(rotationRef.current + (now - last) * DEG_PER_MS);
      last = now;
      idleRafRef.current = requestAnimationFrame(tick);
    };
    idleRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (idleRafRef.current) cancelAnimationFrame(idleRafRef.current);
      idleRafRef.current = null;
    };
  }, [orbOpen, spinEnabled, spinPaused, spinSeconds, setRotation]);

  // ---- geometry (stage sized to what is actually on it) ------------------
  // The compact stage spans the full width of whatever hosts it (measured by
  // onLayout, since a dashboard band is narrower than the window), and the
  // disc fills that width instead of sitting as a fixed 234px puck in the
  // middle of it. The gutter left over is what the prong / project labels
  // (which hang off the rim) need to stay on canvas.
  const STAGE_W = compact ? Math.max(hostW || width - 52, 260) : Math.min(width - 24, 420);
  const HALF = STAGE_W / 2;
  const BASE_RX_OUTER = HALF * (compact ? 0.68 : 0.9);
  // Zoom changes the orbital geometry, not the parent View. Scaling the parent
  // also scaled/shifted the controls and was ignored by parts of RN Web.
  const RX_OUTER = BASE_RX_OUTER * zoom;

  // One compass, up to two stacked levels: the same five-prong disc drawn for
  // ORDER (always) and, once unlocked, SHAPE — offset vertically. They share
  // ONE rotation/zoom/tilt (one D-pad), one toggle set, and one orb. Only
  // prong labels differ per level. STAGE is not a level here — it's the
  // chronological S/T/A/G/E orbit baked into every disc (ORB_STAGES below).
  const DISC_SYSTEMS: RingSystemId[] = shapeUnlocked ? ['order', 'shape'] : ['order'];
  const DISC_GAP = compact ? 116 : 150; // vertical spacing between levels
  const discOffset = (li: number) => (li - (DISC_SYSTEMS.length - 1) / 2) * DISC_GAP; // centred, evenly spaced

  // How far the deepest sector's project rows hang below the disc nodes — the
  // stage has to make room for them, and give it back when TKNE is toggled off.
  const projDrop = useMemo(() => {
    let deepest = 0;
    for (const list of Object.values(oracleProjects)) {
      if (!list?.length) continue;
      const drop = list.reduce((sum, p) => sum + projRowHeight(p), PROJ_ROW_DROP);
      deepest = Math.max(deepest, drop);
    }
    return deepest;
  }, [oracleProjects]);

  // COMPACT: the canvas is measured from the CURRENT tilt/zoom, not the maximum
  // the D-pad allows — a flat, zoomed-out disc must not reserve the height of
  // an upright, zoomed-in one. Raising the disc (or opening an orb) grows the
  // dashboard band; flattening it shrinks it back.
  // FULLSCREEN: keep the fixed worst-case canvas — that layout has the whole
  // screen to itself and must not jump around under the D-pad.
  const LABEL_PAD = 28; // prong labels sit outside the rim
  const levelSpread = ((DISC_SYSTEMS.length - 1) / 2) * DISC_GAP;
  const discRy = RX_OUTER * tilt;
  const aboveH = discRy + levelSpread + LABEL_PAD;
  const belowH =
    discRy + levelSpread + LABEL_PAD + (levelOn.shape && showProjects ? projDrop : 0);
  const fixedCompassH = Math.round(BASE_RX_OUTER * ZOOM_MAX * RY_FACTOR_MAX * 2 + 56);
  const compassH = compact ? Math.round(aboveH + belowH) : fixedCompassH;
  const CX = HALF;
  const CY = compact ? Math.round(aboveH) : compassH / 2;
  // The centre orb belongs to the disc, not the widened compact control stage.
  const CENTER_ORB_R = compact ? 13 : STAGE_W * 0.05;
  // The opened orb contains navigation, a title, and five ORDER rows. A 100px
  // compact orb forces all of those layers on top of one another, so retain a
  // readable dashboard size while keeping it smaller than fullscreen.
  const ORB = compact ? 220 : Math.min(252, Math.round(STAGE_W * 0.74));
  const ORB_BOTTOM = 6;
  const CONNECTOR_GAP = 26;
  // Compact: only an OPEN orb occupies stage height; a closed one reserves
  // nothing. Fullscreen keeps the orb's slot permanently.
  const VISUAL_STAGE_H = compassH + (orbOpen || !compact ? CONNECTOR_GAP + ORB + ORB_BOTTOM : 0);
  const STAGE_H = VISUAL_STAGE_H;
  const rowH = Math.min(26, Math.round(ORB * 0.1));
  const GRID_W = Math.round(ORB * 0.76);

  const ept = (rx: number, ry: number, deg: number) => ({ x: CX + rx * Math.cos(radOf(deg)), y: CY + ry * Math.sin(radOf(deg)) });
  const scalePt = (p: { x: number; y: number }) => ({ x: CX + (p.x - CX) * systemScale, y: CY + (p.y - CY) * systemScale });
  const ringRx = (stage: Stage) => RX_OUTER * RX_RATIO[ORB_STAGES.indexOf(stage)];
  const nodePos = (spoke: Spoke, i: number, count: number, stage: Stage) => {
    const rx = ringRx(stage);
    const { mag } = driftOf(spoke, stage);
    // The spoke is the counter-clockwise/left boundary of this sector. Drift
    // always moves inward (clockwise) from that boundary, so a node can never
    // enter either neighbouring sector regardless of its source direction.
    const inwardDrift = Math.min(360 / count, Math.max(0, mag * MAXOFF));
    return scalePt(ept(rx, rx * tilt, spokeAngle(i, count) + rotation + inwardDrift));
  };
  const anchorPos = (i: number, count: number, stage: Stage) =>
    scalePt(ept(ringRx(stage), ringRx(stage) * tilt, spokeAngle(i, count) + rotation));

  // ---- projects geometry. A project MIRRORS the disc's five ring nodes
  // (E,G,A,T,S — ORB_STAGES): its five orbs sit DIRECTLY BELOW the disc's
  // nodes — identical x, spacing, and size — dropped down by a fixed row
  // offset. Successive projects mirror again, one row lower. No angle unless
  // drift (real projects have no drift signal yet, so it's always 0). They
  // track the disc's rotation because they reuse anchorPos per stage.
  // (PROJ_ROW_DROP / PROJ_ROW_GAP / SUB_* are module constants — the stage
  // height calculation above needs them before this point.)
  /** Ratio along the spoke for step `k` of `n`. Steps run OUTWARD-IN: step 1
   *  (k=0) sits on the outer rim and the last step lands nearest the centre,
   *  so a project reads from the rim inward. n===5 reproduces RX_RATIO reversed. */
  const stepRatio = (k: number, n: number) =>
    n <= 1 ? 1 : 1 - ((1 - RX_RATIO[0]) * k) / (n - 1);
  /** A Directions step's orb position: `si`/`count` locate the spoke, `top` is
   *  the project row's y-offset below the disc node. */
  const stepOrbPos = (si: number, count: number, top: number, k: number, n: number) => {
    const rx = RX_OUTER * stepRatio(k, n);
    const p = scalePt(ept(rx, rx * tilt, spokeAngle(si, count) + rotation));
    return { x: p.x, y: p.y + top };
  };
  // ---- animation ---------------------------------------------------------
  const animateRotation = useCallback(
    (target: number, cb?: () => void) => {
      const start = rotationRef.current;
      const t0 = Date.now();
      const dur = 560;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const step = () => {
        const p = Math.min(1, (Date.now() - t0) / dur);
        const e = 1 - Math.pow(1 - p, 3);
        setRotation(start + (target - start) * e);
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else {
          setRotation(target);
          cb?.();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [setRotation]
  );

  const rotateToBottom = useCallback(
    (i: number, count: number, cb?: () => void) => {
      let target = 90 - spokeAngle(i, count);
      while (target - rotationRef.current > 180) target -= 360;
      while (target - rotationRef.current < -180) target += 360;
      animateRotation(target, cb);
    },
    [animateRotation]
  );

  const pulseOrb = useCallback(
    (to: number) => {
      Animated.sequence([
        Animated.timing(orbScale, { toValue: to, duration: 150, useNativeDriver: true }),
        Animated.timing(orbScale, { toValue: 1, duration: 220, useNativeDriver: true }),
      ]).start();
    },
    [orbScale]
  );

  const focusNode = useCallback(
    (sys: RingSystemId, spoke: Spoke, i: number, count: number, level: number) => {
      setCurSystem(sys);
      setCurSpokeId(spoke.id);
      setCurLevel(level);
      setOrbOpen(true);
      setSystemScale(1);
      rotateToBottom(i, count);
    },
    [rotateToBottom]
  );

  const navSector = useCallback(
    (dir: 'left' | 'right') => {
      const list = spokesFor(curSystem);
      setCurSpokeId((cur) => {
        if (!cur) return cur;
        const idx = list.findIndex((s) => s.id === cur);
        if (idx < 0) return cur;
        const nextIdx = (idx + (dir === 'right' ? 1 : -1) + list.length) % list.length;
        rotateToBottom(nextIdx, list.length);
        return list[nextIdx].id;
      });
    },
    [rotateToBottom, spokesFor, curSystem]
  );

  const navLevel = useCallback(
    (dir: 'up' | 'down') => {
      setCurLevel((l) => (dir === 'up' ? Math.max(0, l - 1) : Math.min(4, l + 1)));
      pulseOrb(dir === 'up' ? 0.9 : 1.06);
      setSystemScale(dir === 'up' ? 1.14 : 0.9);
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setSystemScale(1), 300);
    },
    [pulseOrb]
  );

  const fireChildStub = useCallback((dir: 'up' | 'down' | 'left' | 'right') => {
    const label: Record<string, string> = {
      up: '+ Project (TKNE) — above',
      right: '+ Recurring (THEMIS) — right',
      down: '+ Task (KYRS) — below',
      left: '+ Habit (MoyRi) — left',
    };
    setToast(label[dir]);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1600);
  }, []);

  // Single tap navigates; double tap (within 240ms) fires the child stub.
  const handleArrow = useCallback(
    (dir: 'up' | 'down' | 'left' | 'right') => {
      const existing = arrowTimers.current[dir];
      if (existing) {
        clearTimeout(existing);
        arrowTimers.current[dir] = null;
        fireChildStub(dir);
        return;
      }
      arrowTimers.current[dir] = setTimeout(() => {
        arrowTimers.current[dir] = null;
        if (dir === 'left' || dir === 'right') navSector(dir);
        else navLevel(dir);
      }, 240);
    },
    [navSector, navLevel, fireChildStub]
  );

  const slideAgenda = useCallback(
    (open: boolean) => {
      setAgendaOpen(open);
      Animated.timing(agendaY, { toValue: open ? 0 : AGENDA_BODY_H, duration: 280, useNativeDriver: true }).start();
    },
    [agendaY]
  );

  // Double-tap (within DOUBLE_TAP_MS) on a task-sourced agenda row: collapse
  // the sheet, ensure the projects layer is on, highlight the linked
  // project/sector for HIGHLIGHT_MS, and spin the compass to its sector.
  // No-op (still single-tap-safe) when the task has no link.
  const handleAgendaEntryPress = useCallback(
    (entry: DayEntry) => {
      if (!entry.id.startsWith('task-')) return;
      const key = entry.id;
      const existing = agendaTapTimers.current[key];
      if (existing) {
        clearTimeout(existing);
        agendaTapTimers.current[key] = null;

        const taskId = key.slice('task-'.length);
        const link = taskLinks.find((l) => l.taskId === taskId);
        if (!link || (!link.projectId && !link.sector)) return;

        slideAgenda(false);
        setShowProjects(true);
        setHighlight({ projectId: link.projectId, sector: link.sector });
        if (highlightTimer.current) clearTimeout(highlightTimer.current);
        highlightTimer.current = setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
        if (link.sector) {
          const si = shapeIndexOfSector(link.sector);
          if (si >= 0) rotateToBottom(si, shapeSpokes.length);
        }
        return;
      }
      agendaTapTimers.current[key] = setTimeout(() => {
        agendaTapTimers.current[key] = null;
      }, DOUBLE_TAP_MS);
    },
    [taskLinks, slideAgenda, rotateToBottom, shapeIndexOfSector, shapeSpokes.length]
  );

  // ---- derived -----------------------------------------------------------
  const orbStage = ORB_STAGES[curLevel];
  const popupTool = popup ? DEMO_TOOL_BY_GEAR[popup.gearIdx] : null;
  const noteKey = (gearIdx: number) => `${curSpokeId}|${orbStage}|${gearIdx}`;
  const orbTopY = VISUAL_STAGE_H - ORB_BOTTOM - ORB;

  // Resolve the open orb's spoke within its level's spoke list.
  const curSpokeList = spokesFor(curSystem);
  const curSpokeIdx = curSpokeId ? curSpokeList.findIndex((s) => s.id === curSpokeId) : -1;
  const curSpoke = curSpokeIdx >= 0 ? curSpokeList[curSpokeIdx] : null;

  // The tapped node lives on one of the three stacked levels, offset vertically.
  const curSystemOffset = discOffset(DISC_SYSTEMS.indexOf(curSystem));
  let connectorPath: string | null = null;
  if (orbOpen && curSpoke) {
    const src = nodePos(curSpoke, curSpokeIdx, curSpokeList.length, orbStage);
    const srcY = src.y + curSystemOffset;
    const y2 = orbTopY + 6;
    const midY = (srcY + y2) / 2;
    connectorPath = `M${src.x},${srcY} C${src.x},${midY} ${CX},${midY} ${CX},${y2}`;
  }

  // ---- task→project "strings" — in-range links, then grouped so each
  // project's dots stack horizontally under its current-stage orb (or, for
  // sector-only links, near the sector's Aim anchor). Today/Week toggle
  // controls the range; Week clusters everything into the same lanes, capped
  // per project.
  const stringsInRange = levelOn.shape && showProjects
    ? taskLinks.filter((l) =>
        stringsRange === 'today' ? sameCalendarDate(l.start, now) : (() => {
          const { start, end } = currentWeekRange(now);
          return l.start >= start && l.start < end;
        })()
      )
    : [];

  interface StringGroup {
    key: string;
    sector: Sector;
    projectId: string | null;
    anchor: { x: number; y: number };
    links: OracleTaskLink[];
  }
  const stringGroups: StringGroup[] = [];
  if (stringsInRange.length > 0) {
    const byKey = new Map<string, StringGroup>();
    const count = shapeSpokes.length;
    for (const link of stringsInRange) {
      if (!link.sector) continue;
      // Skip tasks whose sector the user has disabled — it has no spoke to hang from.
      const si = shapeIndexOfSector(link.sector);
      if (si < 0) continue;
      const groupKey = link.projectId ?? `sector:${link.sector}`;
      let group = byKey.get(groupKey);
      if (!group) {
        let anchor: { x: number; y: number };
        if (link.projectId) {
          const proj = (oracleProjects[link.sector] ?? []).find((p) => p.id === link.projectId);
          const stage = proj ? ORB_STAGES[Math.min(proj.currentStage - 1, ORB_STAGES.length - 1)] : ORB_STAGES[0];
          anchor = anchorPos(si, count, stage);
        } else {
          anchor = anchorPos(si, count, 'A');
        }
        group = { key: groupKey, sector: link.sector, projectId: link.projectId, anchor, links: [] };
        byKey.set(groupKey, group);
      }
      group.links.push(link);
    }
    stringGroups.push(...byKey.values());
  }

  const isHighlighted = (group: StringGroup): boolean => {
    if (!highlight) return false;
    if (highlight.projectId) return highlight.projectId === group.projectId;
    return !!highlight.sector && !group.projectId && highlight.sector === group.sector;
  };
  const anyHighlight = !!highlight;

  const arrowSpec: Array<{ dir: 'up' | 'down' | 'left' | 'right'; glyph: string; pos: any }> = [
    { dir: 'up', glyph: '▲', pos: { top: -2, left: ORB / 2 - 12 } },
    { dir: 'down', glyph: '▼', pos: { bottom: -2, left: ORB / 2 - 12 } },
    { dir: 'left', glyph: '◀', pos: { left: -2, top: ORB / 2 - 12 } },
    { dir: 'right', glyph: '▶', pos: { right: -2, top: ORB / 2 - 12 } },
  ];

  return (
    <View
      style={[styles.root, compact && styles.compactRoot, compact && { height: STAGE_H }]}
      onLayout={compact ? (e) => setHostW(Math.round(e.nativeEvent.layout.width)) : undefined}
    >
      <ScrollView
        style={compact ? { height: STAGE_H } : undefined}
        contentContainerStyle={[styles.content, compact && styles.compactContent]}
        scrollEnabled={!compact && scroll}
      >
        <View style={{ width: STAGE_W, height: STAGE_H }}>
          {/* Level toggles (top-left): ORDER (always) and, once unlocked,
              SHAPE — turning one off hides that entire ring-system level.
              Each disc's own S/T/A/G/E orbits are ORDER's chronological
              layer (Step→...→Endeavour), not a separate level. */}
          <>
            {DISC_SYSTEMS.map((sys, li) => (
              <Pressable
                key={`lvl-${sys}`}
                style={[
                  styles.telosToggle,
                  compact && styles.compactTelosToggle,
                  { top: 6 + li * (compact ? 30 : 34) },
                  levelOn[sys] && styles.projToggleOn,
                ]}
                onPress={() => setLevelOn((v) => ({ ...v, [sys]: !v[sys] }))}
              >
                <GlassFill radius={999} />
                <Text style={[styles.projToggleText, levelOn[sys] && styles.projToggleTextOn]}>
                  {sys.toUpperCase()} {levelOn[sys] ? '◉' : '○'}
                </Text>
              </Pressable>
            ))}
            {/* TKNE — the projects layer that lives under the SHAPE level. */}
            <Pressable
              key="lvl-tkne"
              style={[
                styles.telosToggle,
                compact && styles.compactTelosToggle,
                // Directly under the last level toggle — no dead rows between.
                { top: 6 + DISC_SYSTEMS.length * (compact ? 30 : 34) },
                showProjects && styles.projToggleOn,
              ]}
              onPress={() => setShowProjects((v) => !v)}
            >
              <GlassFill radius={999} />
              <Text style={[styles.projToggleText, showProjects && styles.projToggleTextOn]}>
                TKNE {showProjects ? '◉' : '○'}
              </Text>
            </Pressable>
          </>

          {/* Compass */}
          <Svg width={STAGE_W} height={compassH} style={StyleSheet.absoluteFill}>
            {/* Filled translucent sector wedges (pie slices from centre to the
                outer ring, ±36° around each spoke). Drawn first so rings and
                nodes sit on top. */}
            {DISC_SYSTEMS.map((sys, li) => levelOn[sys] ? (
            <G key={`disc-${sys}`} transform={`translate(0, ${discOffset(li)})`}>
            {spokesFor(sys).map((spoke, si, arr) => {
              const count = arr.length;
              const seg = 360 / count;
              const boundary = spokeAngle(si, count) + rotation;
              const rxO = RX_OUTER * systemScale;
              const ryO = rxO * tilt;
              const STEPS = 10;
              let d = `M${CX},${CY}`;
              for (let k = 0; k <= STEPS; k++) {
                // The sector occupies the segment immediately to the right of its
                // spoke, placing that spoke flush on the opposite edge.
                const a = boundary + (seg * k) / STEPS;
                const p = ept(rxO, ryO, a);
                d += ` L${p.x.toFixed(1)},${p.y.toFixed(1)}`;
              }
              d += ' Z';
              return <Path key={`wedge-${spoke.id}`} d={d} fill={spoke.color} fillOpacity={0.12} stroke={spoke.color} strokeOpacity={0.22} strokeWidth={1.2} />;
            })}

            {/* Ring ellipses — Today/Aim/Goal/Endeavour rings tint amber when
                the STAGE ladder's live pace projection for that rung's open
                window is behind pace (currentWindow.onTrack === false); the
                calm default grey stands for on-track/untinted (Step ring). */}
            {ORB_STAGES.map((stage) => {
              const behindPace =
                TINTED_STAGES.includes(stage) && !ladder.rungs[STAGE_TO_RUNG[stage]].currentWindow.onTrack;
              return (
                <React.Fragment key={`ring-${stage}`}>
                  <Ellipse
                    cx={CX}
                    cy={CY}
                    rx={ringRx(stage) * systemScale}
                    ry={ringRx(stage) * tilt * systemScale}
                    stroke={behindPace ? SEV_STROKE.amber : t.goldGlow}
                    strokeWidth={4}
                    strokeOpacity={0.14}
                    fill="none"
                  />
                  <Ellipse
                    cx={CX}
                    cy={CY}
                    rx={ringRx(stage) * systemScale}
                    ry={ringRx(stage) * tilt * systemScale}
                    stroke={behindPace ? SEV_STROKE.amber : stage === 'S' || stage === 'T' ? t.navyLine : t.track}
                    strokeWidth={behindPace ? 1.4 : 0.8}
                    strokeOpacity={behindPace ? 0.85 : 1}
                    fill="none"
                  />
                  {spokesFor(sys).flatMap((spoke, si, arr) => {
                    const count = arr.length;
                    const seg = 360 / count;
                    return [1, 2, 3].map((step) => {
                      const angle = spokeAngle(si, count) + rotation + (seg * step) / 4;
                      const point = scalePt(ept(ringRx(stage), ringRx(stage) * tilt, angle));
                      return (
                        <Circle
                          key={`ring-guide-${stage}-${spoke.id}-${step}`}
                          cx={point.x}
                          cy={point.y}
                          r={1.35}
                          fill={t.ivoryFaint}
                          opacity={0.72}
                        />
                      );
                    });
                  })}
                </React.Fragment>
              );
            })}

            {/* Sector-coloured spoke skeleton */}
            {spokesFor(sys).map((spoke, si, arr) => {
              const count = arr.length;
              const anchors = ORB_STAGES.map((stage) => anchorPos(si, count, stage));
              const outer = anchors[anchors.length - 1];
              return (
                <React.Fragment key={`sk-${spoke.id}`}>
                  <Line x1={CX} y1={CY} x2={outer.x} y2={outer.y} stroke={spoke.color} strokeWidth={0.7} strokeOpacity={0.5} />
                  {anchors.slice(0, -1).map((p, i) => (
                    <Line
                      key={`cl-${spoke.id}-${i}`}
                      x1={p.x}
                      y1={p.y}
                      x2={anchors[i + 1].x}
                      y2={anchors[i + 1].y}
                      stroke={spoke.color}
                      strokeWidth={0.9}
                      strokeOpacity={0.28}
                    />
                  ))}
                  {anchors.map((p, i) => (
                    <Circle key={`an-${spoke.id}-${i}`} cx={p.x} cy={p.y} r={3.5} fill={t.navyRaised} opacity={0.9} />
                  ))}
                </React.Fragment>
              );
            })}

            {/* Colour nodes — the original top-layer disc orbs (TELOS layer). */}
            {showTelos &&
              spokesFor(sys).map((spoke, si, arr) => {
                const count = arr.length;
                const gear = sys === 'order' ? gearOfSpoke(spoke) : null;
                return ORB_STAGES.map((stage) => {
                  const p = nodePos(spoke, si, count, stage);
                  const { color } = driftOf(spoke, stage);
                  const back = Math.sin(radOf(spokeAngle(si, count) + rotation)) < 0;
                  const active = orbOpen && curSystem === sys && curSpokeId === spoke.id && orbStage === stage;
                  const r = NODE_R * (back ? 0.82 : 1) * (active ? 1.2 : 1);
                  // ORDER gears fill in only as far as the user has actually
                  // climbed. ORB_STAGES runs inner→outer (E…S) but the ladder
                  // runs STEP→SECURED, so the rung is the reversed index: the
                  // outer Step ring is rung 0. Rungs at or below the gear's
                  // current tier wear the gear's colour (the tier itself full
                  // strength, the ones already banked slightly softened);
                  // rungs not yet reached are semi-transparent grey.
                  const rungIdx = ORB_STAGES.length - 1 - ORB_STAGES.indexOf(stage);
                  const tier = gear ? gearTierIdx[gear] : null;
                  const reached = tier === null || rungIdx <= tier;
                  const isCurrentRung = tier !== null && rungIdx === tier;
                  const selectedSpoke = orbOpen && curSystem === sys && curSpokeId === spoke.id;
                  const gold = reached && (active || isCurrentRung || selectedSpoke);
                  const fill = !reached ? t.ivoryFaint : gold ? t.goldBright : shade(spoke.color, RING_SHADE[stage]);
                  const stroke = !reached ? t.ivoryFaint : gold ? t.gold : SEV_STROKE[color];
                  const baseOpacity = reached ? (isCurrentRung || tier === null ? 1 : 0.85) : 0.28;
                  return (
                    <React.Fragment key={`node-${spoke.id}-${stage}`}>
                      {reached ? (
                        <Circle
                          cx={p.x}
                          cy={p.y}
                          r={r + 4}
                          fill={gold ? t.goldGlow : spoke.color}
                          fillOpacity={gold ? 0.9 : 0.16}
                          opacity={baseOpacity * (back ? 0.78 : 1)}
                        />
                      ) : null}
                      <Circle
                        cx={p.x}
                        cy={p.y}
                        r={r}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={active ? 3 : isCurrentRung ? 2.6 : 2}
                        opacity={baseOpacity * (back ? 0.78 : 1)}
                      />
                    </React.Fragment>
                  );
                });
              })}

            {/* Sector labels — larger, seated just outside the outer ring. */}
            {spokesFor(sys).map((spoke, si, arr) => {
              const count = arr.length;
              const p = scalePt(ept(RX_OUTER + 22, RX_OUTER * tilt + 22, spokeAngle(si, count) + rotation));
              return (
                <SvgText
                  key={`sl-${spoke.id}`}
                  x={p.x}
                  y={p.y}
                  fontSize={13}
                  fontFamily={DISPLAY_FONT}
                  letterSpacing={2}
                  fill={spoke.color}
                  textAnchor="middle"
                >
                  {spoke.glyph ? `${spoke.glyph} ` : ''}{spoke.label.toUpperCase()}
                </SvgText>
              );
            })}

            {/* Centre hub */}
            <Circle cx={CX} cy={CY} r={CENTER_ORB_R} fill={t.navyRaised} stroke={t.gold} strokeWidth={1.5} />
            </G>
            ) : null)}
          </Svg>

          {/* ---- Projects: real designs (useOracleProjects) — each project
              mirrors all five of the disc's ring nodes (E,G,A,T,S) directly
              below them (same x, spacing, size). A stage orb renders solid
              once that forecast step is checked, hollow otherwise. Successive
              projects mirror again one row lower. Sectors with no projects
              render nothing. ---- */}
          {levelOn.shape && showProjects ? (
            <View pointerEvents="none" style={StyleSheet.absoluteFill}>
              <Svg width={STAGE_W} height={STAGE_H}>
                <G transform={`translate(0, ${discOffset(0)})`}>
                {shapeSpokes.map((spoke, sectorIdx, arr) => {
                  // Projects only attach to built-in sectors (custom spokes have
                  // no designs). Disabled sectors aren't in shapeSpokes at all.
                  if (!spoke.key) return null;
                  const count = arr.length;
                  const projects = oracleProjects[spoke.key] ?? [];
                  if (projects.length === 0) return null;
                  // Paler, greyer version of the sector color — the projects
                  // layer recedes below the main disc instead of matching it.
                  const col = spoke.color;
                  // Each project row starts below the previous row's deepest
                  // sub-step column, so sub-steps can hang without collision.
                  let rowTop = PROJ_ROW_DROP;
                  const rowTops = projects.map((p: OracleProject) => {
                    const top = rowTop;
                    rowTop += projRowHeight(p);
                    return top;
                  });
                  return projects.map((proj: OracleProject, i) => {
                    // One orb per ORDER "Directions" step. Colour is the step's
                    // P/S/C status chip — grey Pending / green Started / blue
                    // Completed (STEP_STATUS_META, shared with TKNE). Drift no
                    // longer tints the orbs (it would fight the status colour);
                    // it still pulls the row left and reddens the project name.
                    const n = proj.steps.length;
                    const top = rowTops[i];
                    const rowScore = Math.max(0, ...proj.drift.map((d) => d?.score ?? 0)); // 0..4
                    const orbs = proj.steps.map((step, k) => {
                      const base = stepOrbPos(sectorIdx, count, top, k, n);
                      return {
                        step,
                        x: base.x - rowScore * DRIFT_PX_PER_SCORE, // pull left by drift
                        y: base.y,
                      };
                    });
                    const anyDrift = rowScore > 0;
                    const nameStroke = anyDrift ? t.lapsed : col;
                    // Steps run outward-in, so step 1 (orbs[0]) is the RIM orb
                    // and the final step is the one nearest the centre. The
                    // connector hangs off the inner end (under the disc's
                    // innermost ring node); the project name sits past the rim.
                    const outerOrb = orbs[0];
                    const innerOrb = orbs[orbs.length - 1];
                    if (!outerOrb || !innerOrb) return null; // project with no Directions steps yet
                    const prevN = i > 0 ? Math.max(1, projects[i - 1].steps.length) : 1;
                    const attach =
                      i === 0
                        ? anchorPos(sectorIdx, count, ORB_STAGES[0])
                        : stepOrbPos(sectorIdx, count, rowTops[i - 1], prevN - 1, prevN);
                    return (
                      <React.Fragment key={`proj-${spoke.id}-${proj.id}`}>
                        {/* vertical connector up to the disc's innermost ring node (or the prev project's inner orb) */}
                        <Line x1={attach.x} y1={attach.y} x2={innerOrb.x} y2={innerOrb.y} stroke={nameStroke} strokeOpacity={anyDrift ? 0.6 : 0.4} strokeWidth={1.2} />
                        {/* horizontal spine across every step orb */}
                        <Line x1={outerOrb.x} y1={outerOrb.y} x2={innerOrb.x} y2={innerOrb.y} stroke={col} strokeOpacity={0.3} strokeWidth={1} />
                        {orbs.map((o, k) => {
                          const stepColor = o.step.status === 'S' ? t.goldBright : col;
                          return (
                            <React.Fragment key={`${proj.id}-${o.step.id}`}>
                              {/* Sub-step column: a vertical line of orbs
                                  dropping straight out of the step's bottom.
                                  Sub-steps carry no status chip, only a
                                  checkbox — checked reads Completed (blue),
                                  unchecked Pending (grey). */}
                              {o.step.substeps.length > 0 ? (
                                <Line
                                  x1={o.x}
                                  y1={o.y}
                                  x2={o.x}
                                  y2={o.y + SUB_DROP + (o.step.substeps.length - 1) * SUB_GAP}
                                  stroke={stepColor}
                                  strokeOpacity={0.35}
                                  strokeWidth={1}
                                />
                              ) : null}
                              {o.step.substeps.map((sub, j) => {
                                const subStatus = sub.checked ? 'C' : 'P';
                                return (
                                  <Circle
                                    key={`${proj.id}-${o.step.id}-${sub.id}`}
                                    cx={o.x}
                                    cy={o.y + SUB_DROP + j * SUB_GAP}
                                    r={SUB_R}
                                    fill={subStatus === 'C' ? col : 'none'}
                                    stroke={col}
                                    strokeWidth={1}
                                    opacity={sub.checked ? 0.95 : 0.5}
                                  />
                                );
                              })}
                              <Circle
                                cx={o.x}
                                cy={o.y}
                                r={NODE_R + 4}
                                fill={o.step.status === 'S' ? t.goldGlow : col}
                                fillOpacity={o.step.status === 'P' ? 0 : o.step.status === 'S' ? 0.9 : 0.16}
                              />
                              <Circle
                                cx={o.x}
                                cy={o.y}
                                r={NODE_R}
                                fill={stepColor}
                                stroke={stepColor}
                                strokeWidth={k === 0 ? 1.8 : 1}
                                // Pending recedes; Started/Completed read solid.
                                opacity={o.step.status === 'P' ? 0.45 : 0.95}
                              />
                            </React.Fragment>
                          );
                        })}
                        {/* Project name at the END of the spoke, styled like
                            the sector labels — seated just past the rim orb
                            (step 1), on whichever side the spoke extends. */}
                        {(() => {
                          const dir = outerOrb.x >= innerOrb.x ? 1 : -1;
                          return (
                            <SvgText
                              x={outerOrb.x + dir * 16}
                              y={outerOrb.y + 3}
                              fontSize={11}
                              fontWeight="800"
                              letterSpacing={0.5}
                              fill={nameStroke}
                              textAnchor={dir === 1 ? 'start' : 'end'}
                            >
                              {proj.name.toUpperCase()}
                            </SvgText>
                          );
                        })()}
                      </React.Fragment>
                    );
                  });
                })}
                </G>
              </Svg>
            </View>
          ) : null}

          {/* ---- Task→project "strings": each scheduled task in range gets
              a small dot stacked below its project's mirrored row (or, for
              sector-only links, near the sector's Aim anchor), with a thin
              thread up to the project's CURRENT-stage orb (or the sector's
              Aim disc node). Today mode shows just today's tasks; Week fans
              out to the whole Mon–Sun week, clustered by project and capped
              at MAX_DOTS_PER_PROJECT dots (+N overflow). A double-tapped
              task's group renders full-saturation with a thicker stroke. ---- */}
          {levelOn.shape && showProjects && stringGroups.length > 0 ? (
            <View pointerEvents="none" style={StyleSheet.absoluteFill}>
              <Svg width={STAGE_W} height={STAGE_H}>
                <G transform={`translate(0, ${discOffset(0)})`}>
                {stringGroups.map((group) => {
                  const lit = isHighlighted(group);
                  const baseCol = lit ? t.goldBright : SECTOR_FILL[group.sector];
                  const dimOthers = anyHighlight && !lit;
                  const visible = group.links.slice(0, MAX_DOTS_PER_PROJECT);
                  const overflow = group.links.length - visible.length;
                  const totalW = (visible.length - 1) * TASK_DOT_GAP;
                  const startX = group.anchor.x - totalW / 2;
                  const dotY = group.anchor.y + PROJ_ROW_DROP * 0.55;
                  return (
                    <React.Fragment key={`str-${group.key}`}>
                      {visible.map((link, i) => {
                        const dx = startX + i * TASK_DOT_GAP;
                        return (
                          <React.Fragment key={`str-${group.key}-${link.taskId}`}>
                            <Line
                              x1={dx}
                              y1={dotY}
                              x2={group.anchor.x}
                              y2={group.anchor.y}
                              stroke={baseCol}
                              strokeWidth={lit ? 1.6 : 0.9}
                              strokeOpacity={dimOthers ? 0.15 : lit ? 0.75 : 0.45}
                            />
                            <Circle
                              cx={dx}
                              cy={dotY}
                              r={TASK_DOT_R}
                              fill={baseCol}
                              opacity={dimOthers ? 0.2 : link.done ? 0.4 : lit ? 1 : 0.85}
                            />
                          </React.Fragment>
                        );
                      })}
                      {overflow > 0 ? (
                        <SvgText
                          x={startX + visible.length * TASK_DOT_GAP}
                          y={dotY + 3}
                          fontSize={8}
                          fontWeight="700"
                          fill={baseCol}
                          opacity={dimOthers ? 0.2 : 1}
                          textAnchor="start"
                        >
                          {`+${overflow}`}
                        </SvgText>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                </G>
              </Svg>
            </View>
          ) : null}

          {/* Highlight connector — dashed thread from the agenda-sheet edge
              up to the double-tapped task's linked project/sector orb, for
              HIGHLIGHT_MS after a double-tap. */}
          {highlight && (highlight.projectId || highlight.sector) ? (
            (() => {
              const group = stringGroups.find((g) => isHighlighted(g));
              if (!group) return null;
              const y2 = VISUAL_STAGE_H;
              const midY = (group.anchor.y + y2) / 2;
              const d = `M${group.anchor.x},${y2} C${group.anchor.x},${midY} ${CX},${midY} ${CX},${group.anchor.y}`;
              return (
                <View pointerEvents="none" style={StyleSheet.absoluteFill}>
                  <Svg width={STAGE_W} height={STAGE_H}>
                    <Path d={d} stroke={t.goldBright} strokeWidth={2} strokeDasharray="5 5" strokeOpacity={0.85} fill="none" />
                  </Svg>
                </View>
              );
            })()
          ) : null}

          {/* Connector overlay — wrapped in a pointerEvents:none View so it
              never intercepts taps meant for the compass tap-layer below. */}
          <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            <Svg width={STAGE_W} height={STAGE_H} style={StyleSheet.absoluteFill}>
              {connectorPath && curSpoke ? (
                <Path d={connectorPath} stroke={t.gold} strokeWidth={1.5} strokeDasharray="4 4" strokeOpacity={0.8} fill="none" />
              ) : null}
            </Svg>
          </View>

          {/* Node tap targets — an invisible layer of hit-circles, one per
              disc node, positioned with the same nodePos() math the disc SVG
              nodes use. Tapping one opens the orb navigator at that
              sector/level. Sits above the disc/projects/strings layers but
              is declared before the orb + D-pad + agenda sheet below, so
              those (rendered after — on top) always keep tap priority over
              it and are never blocked. Only present while the TELOS node
              layer is shown (nothing to tap otherwise). */}
          {showTelos ? (
            <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
              {DISC_SYSTEMS.map((sys, li) =>
                levelOn[sys] && spokesFor(sys).map((spoke, si, arr) =>
                  ORB_STAGES.map((stage, level) => {
                    const p = nodePos(spoke, si, arr.length, stage);
                    return (
                      <Pressable
                        key={`hit-${sys}-${spoke.id}-${stage}`}
                        onPress={() => focusNode(sys, spoke, si, arr.length, level)}
                        hitSlop={4}
                        style={{
                          position: 'absolute',
                          left: p.x - NODE_HIT_R,
                          top: p.y - NODE_HIT_R + discOffset(li),
                          width: NODE_HIT_R * 2,
                          height: NODE_HIT_R * 2,
                          borderRadius: NODE_HIT_R,
                        }}
                      />
                    );
                  })
                )
              )}
            </View>
          ) : null}

          {/* Orb */}
          {orbOpen && curSpoke ? (
            <Animated.View
              style={[
                styles.orb,
                {
                  width: ORB,
                  height: ORB,
                  borderRadius: ORB / 2,
                  bottom: ORB_BOTTOM,
                  left: CX - ORB / 2,
                  transform: [{ scale: orbScale }],
                  backgroundColor: 'transparent',
                  borderColor: t.gold,
                  boxShadow: `0 0 28px ${t.goldGlow}`,
                },
              ]}
            >
              <GlassFill radius={ORB / 2} tone="strong" />
              <Pressable style={styles.orbClose} onPress={() => { setOrbOpen(false); setCurSpokeId(null); setPopup(null); }} hitSlop={10}>
                <Text style={styles.orbCloseText}>✕</Text>
              </Pressable>

              {arrowSpec.map((a) => (
                <Pressable key={a.dir} style={[styles.orbNav, a.pos]} onPress={() => handleArrow(a.dir)} hitSlop={6}>
                  <Text style={styles.orbNavText}>{a.glyph}</Text>
                </Pressable>
              ))}

              {/* Stage pips */}
              <View style={styles.pips}>
                {ORB_STAGES.map((_, i) => (
                  <View
                    key={i}
                    style={{
                      width: i === curLevel ? 8 : 6,
                      height: i === curLevel ? 8 : 6,
                      borderRadius: 4,
                      backgroundColor: i === curLevel ? t.goldBright : t.gold,
                    }}
                  />
                ))}
              </View>

              <Text style={styles.orbTitle}>
                {curSpoke.label.toLowerCase()} · {STAGE_LABELS[orbStage].toLowerCase()} ·{' '}
                {formatLadderRungState(ladder.rungs[STAGE_TO_RUNG[orbStage]])}
              </Text>

              {/* O/R/D/E/R rows */}
              <View style={[styles.orbGrid, { width: GRID_W }]}>
                {GEARS.map((gear, i) => (
                  <View
                    key={gear}
                    style={[styles.orbRow, { height: rowH }, i === GEARS.length - 1 && { borderBottomWidth: 0 }]}
                  >
                    <View style={styles.orbLetterCell}>
                      <Text style={styles.orbLetter}>{GEAR_META[gear].letter}</Text>
                    </View>
                    <Pressable style={[styles.orbValueCell, popup?.gearIdx === i && styles.orbValueCellOn]} onPress={() => setPopup({ gearIdx: i })}>
                      <Text style={[styles.orbValue, popup?.gearIdx === i && styles.orbValueOn]} numberOfLines={1}>
                        {notes[noteKey(i)] ?? ''}
                      </Text>
                    </Pressable>
                  </View>
                ))}
              </View>
            </Animated.View>
          ) : null}

          {/* Tool popup */}
          {popup && popupTool && curSpoke ? (
            <View style={[styles.popup, { left: (STAGE_W - 230) / 2 }]}>
              <GlassFill radius={radii.tile} tone="strong" />
              <Text style={styles.popupTitle}>
                {TOOL_NAMES[popupTool] ?? popupTool.toUpperCase()} — {GEAR_META[GEARS[popup.gearIdx]].letter} tool
              </Text>
              <Text style={styles.popupMeta}>
                {curSpoke.label.toLowerCase()} · {STAGE_LABELS[orbStage].toLowerCase()}
              </Text>
              <TextInput
                value={notes[noteKey(popup.gearIdx)] ?? ''}
                onChangeText={(t) => setNotes((n) => ({ ...n, [noteKey(popup.gearIdx)]: t }))}
                placeholder="Note…"
                placeholderTextColor={t.ivoryFaint}
                multiline
                style={styles.popupInput}
              />
              <View style={styles.popupBtnRow}>
                <Pressable
                  style={styles.popupOpen}
                  onPress={() => {
                    onOpenTool(popupTool);
                    setPopup(null);
                  }}
                >
                  <GlassFill radius={radii.tile} tone="gold" />
                  <Text style={styles.popupOpenText}>Open {TOOL_NAMES[popupTool] ?? popupTool.toUpperCase()}</Text>
                </Pressable>
                <Pressable style={styles.popupClose} onPress={() => setPopup(null)}>
                  <GlassFill radius={radii.tile} />
                  <Text style={styles.popupCloseText}>Close</Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {/* Child-creation stub toast */}
          {toast ? (
            <View style={styles.toast}>
              <GlassFill radius={999} tone="strong" />
              <Text style={styles.toastText}>{toast}</Text>
            </View>
          ) : null}

          {/* Single D-pad controller, seated to the right of the compass at
              its vertical centre. ▲/▼ tilt (raise toward a circle / flatten
              toward a disc); ◀/▶ rotate horizontally; centre = play/pause the
              auto-spin. Zoom +/- sit underneath, scaling the whole stage.
              Available in both full and dashboard layouts. */}
          <View
            style={[
              styles.dpad,
              // Compact: seated bottom-right. 140 = the 0.62-scaled height of
              // the pad plus its zoom row, so the −/+ buttons stay on canvas.
              { top: compact ? Math.max(0, compassH - 140) : compassH / 2 - DPAD / 2, right: 4 },
              compact && { transform: [{ scale: 0.62 }] },
            ]}
          >
              <Pressable
                style={[styles.dpadBtn, styles.dpadUp]}
                onPress={() => setTilt((t) => Math.min(RY_FACTOR_MAX, +(t + 0.06).toFixed(3)))}
              >
                <GlassFill radius={DPAD_BTN / 2} />
                <Text style={styles.dpadText}>▲</Text>
              </Pressable>
              <Pressable
                style={[styles.dpadBtn, styles.dpadDown]}
                onPress={() => setTilt((t) => Math.max(RY_FACTOR_MIN, +(t - 0.06).toFixed(3)))}
              >
                <GlassFill radius={DPAD_BTN / 2} />
                <Text style={styles.dpadText}>▼</Text>
              </Pressable>
              <Pressable
                style={[styles.dpadBtn, styles.dpadLeft]}
                onPress={() => animateRotation(rotationRef.current - 20)}
              >
                <GlassFill radius={DPAD_BTN / 2} />
                <Text style={styles.dpadText}>◀</Text>
              </Pressable>
              <Pressable
                style={[styles.dpadBtn, styles.dpadRight]}
                onPress={() => animateRotation(rotationRef.current + 20)}
              >
                <GlassFill radius={DPAD_BTN / 2} />
                <Text style={styles.dpadText}>▶</Text>
              </Pressable>
              <Pressable style={[styles.dpadBtn, styles.dpadCenter]} onPress={() => setSpinPaused((p) => !p)}>
                <GlassFill radius={DPAD_BTN / 2} tone="gold" />
                <Text style={styles.dpadCenterText}>{spinPaused ? '▶' : '❚❚'}</Text>
              </Pressable>

              <View style={styles.zoomRow}>
                <Pressable
                  style={styles.zoomBtn}
                  onPress={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                >
                  <GlassFill radius={17} />
                  <Text style={styles.zoomText}>−</Text>
                </Pressable>
                <Pressable
                  style={styles.zoomBtn}
                  onPress={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                >
                  <GlassFill radius={17} />
                  <Text style={styles.zoomText}>+</Text>
                </Pressable>
              </View>
          </View>
        </View>
      </ScrollView>

      {/* Persistent agenda bottom sheet. The header bar is always visible at the
          bottom; tapping it slides the body up over the orb / back down.
          Hidden in compact mode. */}
      {!compact && (
        <Animated.View style={[styles.agenda, { transform: [{ translateY: agendaY }] }]}>
          <GlassFill radius={radii.tile} topOnly tone="strong" />
          <Pressable style={styles.agendaHeaderRow} onPress={() => slideAgenda(!agendaOpen)}>
            <Text style={styles.agendaHeader}>Today</Text>
            <Text style={styles.agendaChevron}>{agendaOpen ? '▾' : '▴'}</Text>
          </Pressable>
          <View style={{ height: AGENDA_BODY_H }}>
            <ScrollView>
              {Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i).map((hour) => {
                const isNow = now.getHours() === hour;
                const hourEntries = entriesInHour(dayEntries, hour, now);
                const hourLabel = formatClockTime((() => {
                  const d = new Date(now);
                  d.setHours(hour, 0, 0, 0);
                  return d;
                })());
                return (
                  <View key={hour} style={[styles.hourRow, isNow && styles.hourRowNow]}>
                    <Text style={[styles.hourLabel, isNow && styles.hourLabelNow]}>{hourLabel}</Text>
                    <View style={styles.hourEntries}>
                      {hourEntries.length === 0 ? (
                        <View style={styles.hourLine} />
                      ) : (
                        hourEntries.map((e) =>
                          e.source === 'task' ? (
                            <Pressable key={e.id} onPress={() => handleAgendaEntryPress(e)} hitSlop={4}>
                              <Text
                                style={[styles.hourEntryText, e.completed && styles.hourEntryDone, isNow && styles.hourEntryTextNow]}
                                numberOfLines={1}
                              >
                                {e.title}
                              </Text>
                            </Pressable>
                          ) : (
                            <Text
                              key={e.id}
                              style={[styles.hourEntryText, e.completed && styles.hourEntryDone, isNow && styles.hourEntryTextNow]}
                              numberOfLines={1}
                            >
                              {e.title}
                            </Text>
                          )
                        )
                      )}
                    </View>
                  </View>
                );
              })}
            </ScrollView>
          </View>
        </Animated.View>
      )}
    </View>
  );
}

// ---------- CompassView: Fullscreen wrapper around CompassCore ----------
export function CompassView({ userId, onOpenTool }: { userId: string | undefined; onOpenTool: (toolId: string) => void }) {
  return <CompassCore userId={userId} onOpenTool={onOpenTool} compact={false} />;
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
  root: { flex: 1, overflow: 'hidden' },
  // flexBasis must be 'auto': RNW compiles `flex: 0` to `flex: 0 1 0%`, and a
  // 0% basis overrides the explicit height on web, collapsing the widget to 0.
  compactRoot: { flexGrow: 0, flexShrink: 0, flexBasis: 'auto', overflow: 'visible' },
  content: { padding: 12, paddingBottom: AGENDA_HEADER_H + 24, alignItems: 'center', gap: 10 },
  compactContent: { padding: 0, paddingBottom: 0, alignItems: 'center', gap: 0 },

  headerRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { fontSize: 17, fontWeight: '800', color: t.ivory },
  subtitle: { fontSize: 11.5, color: t.ivorySoft, marginTop: 2, lineHeight: 15 },
  knobOn: { left: 17 },

  agenda: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    boxShadow: `0 -8px 30px ${t.glowSoft}`,
    borderTopLeftRadius: radii.tile,
    borderTopRightRadius: radii.tile,
    borderWidth: StyleSheet.hairlineWidth,
    borderBottomWidth: 0,
    borderColor: t.navyLine,
    zIndex: 100,
    elevation: 24,
  },
  agendaHeaderRow: {
    height: AGENDA_HEADER_H,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: t.lineFaint,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 16,
  },
  agendaHeader: { color: t.ivory, fontSize: 14, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
  agendaChevron: { color: t.gold, fontSize: 16, fontWeight: '700' },
  agendaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  agendaText: { flex: 1, fontSize: 15, color: t.ivory },

  hourRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  hourRowNow: { backgroundColor: c.lime },
  hourLabel: { width: 64, fontSize: 12, color: t.ivorySoft, fontWeight: '600' },
  hourLabelNow: { color: t.goldBright, fontWeight: '800' },
  hourEntries: { flex: 1, gap: 4 },
  hourLine: { height: 1, backgroundColor: t.lineFaint, marginTop: 8 },
  hourEntryText: { fontSize: 14, color: t.ivory, fontWeight: '600' },
  hourEntryTextNow: { color: t.goldBright, fontWeight: '800' },
  hourEntryDone: { color: t.ivoryFaint, textDecorationLine: 'line-through' },

  orb: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: t.gold,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  orbClose: { position: 'absolute', top: 6, right: 16, zIndex: 7 },
  orbCloseText: { fontSize: 13, color: t.ivoryFaint, fontWeight: '700' },
  orbNav: {
    position: 'absolute',
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: t.gold,
    borderWidth: 1,
    borderColor: t.goldBright,
    boxShadow: `0 0 8px ${t.goldGlow}`,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 6,
  },
  orbNavText: { fontSize: 10, color: t.onGold },
  pips: { flexDirection: 'row', gap: 5, marginBottom: 4, alignItems: 'center' },
  orbTitle: { fontSize: 11.5, fontWeight: '700', color: t.goldBright, marginBottom: 7, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
  orbGrid: { borderWidth: 1.5, borderColor: t.gold, borderRadius: 4, overflow: 'hidden' },
  orbRow: { flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: t.gold },
  orbLetterCell: {
    width: 30,
    backgroundColor: t.navy,
    borderWidth: 1.5,
    borderColor: t.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  orbLetter: { fontSize: 15, fontWeight: '700', color: t.goldBright, fontFamily: DISPLAY_FONT },
  orbValueCell: { flex: 1, backgroundColor: t.navy, justifyContent: 'center', paddingHorizontal: 8 },
  orbValueCellOn: { backgroundColor: c.lime },
  orbValue: { fontSize: 11, color: t.ivory, textTransform: 'uppercase' },
  orbValueOn: { color: t.goldBright },

  popup: {
    position: 'absolute',
    top: 20,
    width: 230,
    backgroundColor: 'transparent',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    borderRadius: radii.tile,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    padding: 12,
    zIndex: 40,
  },
  popupTitle: { fontSize: 12.5, fontWeight: '700', color: t.ivory, marginBottom: 3, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
  popupMeta: { fontSize: 10.5, color: t.ivorySoft, marginBottom: 8 },
  popupInput: {
    height: 64,
    backgroundColor: t.night,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    borderRadius: radii.tile,
    padding: 7,
    fontSize: 12,
    color: t.ivory,
    textAlignVertical: 'top',
  },
  popupBtnRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  popupOpen: { backgroundColor: 'transparent', boxShadow: `0 0 14px ${t.goldGlow}`, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 6 },
  popupOpenText: { color: t.onGold, fontSize: 12 },
  popupClose: { backgroundColor: 'transparent', borderWidth: StyleSheet.hairlineWidth, borderColor: t.navyLine, borderRadius: radii.tile, paddingHorizontal: 12, paddingVertical: 6 },
  popupCloseText: { color: t.ivory, fontSize: 12 },

  toast: {
    position: 'absolute',
    top: 8,
    alignSelf: 'center',
    backgroundColor: 'transparent',
    boxShadow: `0 6px 20px ${t.glowSoft}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    zIndex: 50,
  },
  toastText: { color: t.ivory, fontSize: 12, fontWeight: '500' },

  telosToggle: {
    position: 'absolute',
    top: 6,
    left: 6,
    zIndex: 30,
    borderRadius: radii.pill,
    backgroundColor: 'transparent',
    boxShadow: `0 4px 14px ${t.glowSoft}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 92,
    alignItems: 'center',
  },
  compactTelosToggle: {
    top: 6,
    left: 2,
    minWidth: 74,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  projToggle: {
    position: 'absolute',
    top: 40,
    left: 6,
    zIndex: 30,
    borderRadius: radii.pill,
    backgroundColor: 'transparent',
    boxShadow: `0 4px 14px ${t.glowSoft}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 92,
    alignItems: 'center',
  },
  compactProjToggle: {
    top: 34,
    left: 2,
    minWidth: 74,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  projToggleOn: { backgroundColor: c.lime, borderColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` },
  projToggleText: { fontSize: 12, fontWeight: '800', color: t.ivory },
  projToggleTextOn: { color: t.goldBright },
  stringsToggle: {
    position: 'absolute',
    top: 74,
    left: 6,
    zIndex: 30,
    flexDirection: 'row',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    overflow: 'hidden',
  },
  compactStringsToggle: { top: 62, left: 2 },
  stringsToggleBtn: { paddingHorizontal: 10, paddingVertical: 5 },
  stringsToggleBtnOn: { backgroundColor: c.lime },
  stringsToggleText: { fontSize: 10.5, fontWeight: '800', color: t.ivory },
  stringsToggleTextOn: { color: t.goldBright },
  dpad: { position: 'absolute', width: DPAD, height: DPAD, zIndex: 5 },
  dpadBtn: {
    position: 'absolute',
    width: DPAD_BTN,
    height: DPAD_BTN,
    borderRadius: DPAD_BTN / 2,
    backgroundColor: 'transparent',
    boxShadow: `0 4px 14px ${t.glowSoft}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dpadUp: { top: 0, left: DPAD / 2 - DPAD_BTN / 2 },
  dpadDown: { bottom: 0, left: DPAD / 2 - DPAD_BTN / 2 },
  dpadLeft: { left: 0, top: DPAD / 2 - DPAD_BTN / 2 },
  dpadRight: { right: 0, top: DPAD / 2 - DPAD_BTN / 2 },
  dpadCenter: {
    top: DPAD / 2 - DPAD_BTN / 2,
    left: DPAD / 2 - DPAD_BTN / 2,
    borderRadius: DPAD_BTN / 2,
    backgroundColor: 'transparent',
    boxShadow: `0 0 16px ${t.goldGlow}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.goldBright,
  },
  dpadText: { fontSize: 18, color: t.ivory, fontWeight: '900' },
  dpadCenterText: { fontSize: 13, color: t.onGold, fontWeight: '900' },
  zoomRow: {
    position: 'absolute',
    top: DPAD + 10,
    left: 0,
    width: DPAD,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
  },
  zoomBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: 'transparent',
    boxShadow: `0 4px 14px ${t.glowSoft}`,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomText: { fontSize: 18, color: t.ivory, fontWeight: '900', lineHeight: 20 },
}),
);
